import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Offcanvas, OffcanvasBody, OffcanvasHeader, Button, Input, Alert } from 'reactstrap';
import { getDetailShopMenu } from "../../slices/thunks";
import { addItem } from "../../slices/Web/cart/cartSlice";
import { useDispatch, useSelector } from "react-redux";
import { toVND } from "../../helpers/helpers";
import { DEFAULT_PRODUCT_IMAGE } from "../../constants/images";
import { isMenuAvailable } from "../../helpers/menuAvailability";

type OptionValueAvailabilityReason = "inactive" | "sold_out" | null;

const truthyOptionFlags = [
    "true",
    "1",
    "yes",
    "y",
    "available",
    "in_stock",
    "instock",
    "active",
    "enabled",
    "selling",
    "on",
    "open",
];

const falsyOptionFlags = [
    "false",
    "0",
    "no",
    "n",
    "unavailable",
    "sold_out",
    "soldout",
    "out_of_stock",
    "outofstock",
    "inactive",
    "disabled",
    "off",
    "closed",
    "stopped",
    "ended",
    "paused",
];

const soldOutStatusKeywords = new Set([
    "sold_out",
    "soldout",
    "sold-out",
    "sold out",
    "out_of_stock",
    "outofstock",
    "out-of-stock",
    "out of stock",
    "depleted",
    "unavailable",
]);

const inactiveStatusKeywords = new Set([
    "inactive",
    "disabled",
    "off",
    "closed",
    "stopped",
    "ended",
    "paused",
    "archived",
    "hidden",
]);

const activeStatusKeywords = new Set([
    "active",
    "available",
    "enabled",
    "on",
    "open",
    "selling",
    "instock",
    "in_stock",
    "in stock",
]);

const parseBooleanLike = (value: any): boolean | undefined => {
    if (typeof value === "boolean") {
        return value;
    }

    if (typeof value === "number") {
        if (Number.isNaN(value)) {
            return undefined;
        }
        return value !== 0;
    }

    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (normalized.length === 0) {
            return undefined;
        }

        if (truthyOptionFlags.includes(normalized)) {
            return true;
        }

        if (falsyOptionFlags.includes(normalized)) {
            return false;
        }
    }

    return undefined;
};

const parseNumericLike = (value: any): number | undefined => {
    if (typeof value === "number") {
        if (!Number.isFinite(value)) {
            return undefined;
        }
        return value;
    }

    if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed.length === 0) {
            return undefined;
        }
        const parsed = Number(trimmed);
        if (Number.isNaN(parsed)) {
            return undefined;
        }
        return parsed;
    }

    return undefined;
};

const getOptionValueAvailability = (value: any): {
    isAvailable: boolean;
    reason: OptionValueAvailabilityReason;
    statusLabel: string | null;
} => {
    if (!value || typeof value !== "object") {
        return {
            isAvailable: true,
            reason: null,
            statusLabel: null,
        };
    }

    let isActiveFlag: boolean | undefined;
    let isAvailableFlag: boolean | undefined;
    let soldOutFlag: boolean | undefined;

    const updateActiveFlag = (flag: boolean) => {
        if (!flag) {
            isActiveFlag = false;
        } else if (isActiveFlag === undefined) {
            isActiveFlag = true;
        }
    };

    const updateAvailableFlag = (flag: boolean) => {
        if (!flag) {
            isAvailableFlag = false;
        } else if (isAvailableFlag === undefined) {
            isAvailableFlag = true;
        }
    };

    const updateSoldOutFlag = (flag: boolean) => {
        if (flag) {
            soldOutFlag = true;
        } else if (soldOutFlag === undefined) {
            soldOutFlag = false;
        }
    };

    const applyStatusString = (rawStatus: string): boolean => {
        const normalized = rawStatus.trim().toLowerCase();
        if (!normalized) {
            return false;
        }

        if (soldOutStatusKeywords.has(normalized)) {
            updateSoldOutFlag(true);
            updateAvailableFlag(false);
            return true;
        }

        if (inactiveStatusKeywords.has(normalized)) {
            updateActiveFlag(false);
            return true;
        }

        if (activeStatusKeywords.has(normalized)) {
            updateActiveFlag(true);
            updateAvailableFlag(true);
            updateSoldOutFlag(false);
            return true;
        }

        return false;
    };

    const handleActiveCandidate = (candidate: any) => {
        if (typeof candidate === "string" && applyStatusString(candidate)) {
            return;
        }
        const parsed = parseBooleanLike(candidate);
        if (parsed !== undefined) {
            updateActiveFlag(parsed);
        }
    };

    const handleAvailableCandidate = (candidate: any) => {
        if (typeof candidate === "string" && applyStatusString(candidate)) {
            return;
        }
        const parsed = parseBooleanLike(candidate);
        if (parsed !== undefined) {
            updateAvailableFlag(parsed);
            if (!parsed) {
                updateSoldOutFlag(true);
            }
            return;
        }

        const numeric = parseNumericLike(candidate);
        if (numeric !== undefined) {
            updateAvailableFlag(numeric > 0);
            if (numeric <= 0) {
                updateSoldOutFlag(true);
            } else {
                updateSoldOutFlag(false);
            }
        }
    };

    const handleSoldOutCandidate = (candidate: any) => {
        if (typeof candidate === "string" && applyStatusString(candidate)) {
            return;
        }
        const parsed = parseBooleanLike(candidate);
        if (parsed !== undefined) {
            updateSoldOutFlag(parsed);
            if (parsed) {
                updateAvailableFlag(false);
            } else {
                updateAvailableFlag(true);
            }
        }
    };

    const handleStatusCandidate = (candidate: any) => {
        if (typeof candidate === "string" && applyStatusString(candidate)) {
            return;
        }
        const parsed = parseBooleanLike(candidate);
        if (parsed !== undefined) {
            if (parsed) {
                updateActiveFlag(true);
                updateAvailableFlag(true);
                updateSoldOutFlag(false);
            } else {
                updateActiveFlag(false);
                updateSoldOutFlag(true);
                updateAvailableFlag(false);
            }
        }
    };

    const candidateKeys: Record<string, (candidate: any) => void> = {
        active: handleActiveCandidate,
        isActive: handleActiveCandidate,
        enabled: handleActiveCandidate,
        is_enabled: handleActiveCandidate,
        available: handleAvailableCandidate,
        isAvailable: handleAvailableCandidate,
        availability: handleStatusCandidate,
        availabilityStatus: handleStatusCandidate,
        status: handleStatusCandidate,
        state: handleStatusCandidate,
        inStock: handleAvailableCandidate,
        in_stock: handleAvailableCandidate,
        hasStock: handleAvailableCandidate,
        has_stock: handleAvailableCandidate,
        stock: handleAvailableCandidate,
        quantity: handleAvailableCandidate,
        qty: handleAvailableCandidate,
        inventory: handleAvailableCandidate,
        remaining: handleAvailableCandidate,
        availableQuantity: handleAvailableCandidate,
        available_quantity: handleAvailableCandidate,
        soldOut: handleSoldOutCandidate,
        sold_out: handleSoldOutCandidate,
        isSoldOut: handleSoldOutCandidate,
        outOfStock: handleSoldOutCandidate,
        out_of_stock: handleSoldOutCandidate,
        is_out_of_stock: handleSoldOutCandidate,
    };

    Object.entries(candidateKeys).forEach(([key, handler]) => {
        if (Object.prototype.hasOwnProperty.call(value, key)) {
            handler((value as Record<string, any>)[key]);
        }
    });

    const isInactive = isActiveFlag === false;
    const isSoldOut = soldOutFlag === true || isAvailableFlag === false;
    const isAvailable = !isInactive && !isSoldOut;

    let reason: OptionValueAvailabilityReason = null;
    if (!isAvailable) {
        if (isInactive) {
            reason = "inactive";
        } else if (isSoldOut) {
            reason = "sold_out";
        }
    }

    const statusLabel =
        reason === "inactive" ? "Ngưng bán" : reason === "sold_out" ? "Hết hàng" : null;

    return {
        isAvailable,
        reason,
        statusLabel,
    };
};

interface DetailMenuOffcanvasProps {
    menuId: string;
    isOpen: boolean;
    onClose?: () => void;
    onAddedToCart?: () => void;
    initialSelectedOptions?: any;
    initialNote?: string;
    initialQuantity?: number;
    onSave?: (item: any) => void;
}

const DetailMenuOffcanvas: React.FC<DetailMenuOffcanvasProps> = ({
    menuId,
    isOpen,
    onClose,
    onAddedToCart,
    initialSelectedOptions,
    initialNote,
    initialQuantity,
    onSave,
}) => {
    const detailShopMenu = useSelector((state: any) => state.DetailShopMenu.detailShopMenu);
    const dispatch: any = useDispatch();
    useEffect(() => {
        dispatch(getDetailShopMenu(menuId));
    }, [menuId, dispatch]);

    const [defaultCounter, setdefaultCounter] = useState(1);
    const [selectedOptions, setSelectedOptions] = useState<any>({});
    const [note, setNote] = useState("");
    const menuOptions = useMemo(() => {
        const rawOptions = Array.isArray(detailShopMenu?.options)
            ? (detailShopMenu?.options as any[])
            : Array.isArray(detailShopMenu?.menu_options)
              ? (detailShopMenu?.menu_options as any[])
              : [];

        if (rawOptions.length === 0) {
            return [];
        }

        return rawOptions.map((option) => {
            if (!option || typeof option !== "object") {
                return option;
            }

            const optionValues = Array.isArray(option.values) ? option.values : [];

            return {
                ...option,
                type: option.type ?? option.selection_type ?? option.selectionType,
                values: optionValues.map((value: any) => {
                    if (!value || typeof value !== "object") {
                        return value;
                    }

                    return {
                        ...value,
                        name: value.name ?? value.label,
                        price: value.price ?? value.price_modifier,
                    };
                }),
            };
        });
    }, [detailShopMenu]);

    const sanitizeSelectedOptions = useCallback(
        (selections: any) => {
            if (!selections || typeof selections !== "object") {
                return {};
            }

            if (menuOptions.length === 0) {
                return selections;
            }

            let changed = false;
            const sanitized: Record<string | number, any> = {};

            Object.entries(selections).forEach(([optionKey, selected]) => {
                const menuOption = menuOptions.find(
                    (option: any) => String(option?.id) === optionKey
                );

                if (!menuOption) {
                    changed = true;
                    return;
                }

                const optionValues = Array.isArray(menuOption.values)
                    ? (menuOption.values as any[])
                    : [];

                if (menuOption.type === "multiple") {
                    if (!Array.isArray(selected)) {
                        changed = true;
                        return;
                    }

                    const filtered = selected
                        .map((item: any) =>
                            optionValues.find(
                                (menuValue: any) =>
                                    String(menuValue?.id) === String(item?.id)
                            )
                        )
                        .filter(
                            (matched: any) =>
                                matched && getOptionValueAvailability(matched).isAvailable
                        );

                    if (filtered.length > 0) {
                        sanitized[menuOption.id] = filtered;
                    }

                    if (filtered.length !== selected.length) {
                        changed = true;
                    }
                } else {
                    if (!selected || typeof selected !== "object") {
                        changed = true;
                        return;
                    }

                    const matched = optionValues.find(
                        (menuValue: any) =>
                            String(menuValue?.id) === String((selected as any).id)
                    );

                    if (matched && getOptionValueAvailability(matched).isAvailable) {
                        sanitized[menuOption.id] = matched;
                    } else {
                        changed = true;
                    }
                }
            });

            return changed ? sanitized : selections;
        },
        [menuOptions]
    );

    useEffect(() => {
        setSelectedOptions(sanitizeSelectedOptions(initialSelectedOptions || {}));
        setNote(initialNote || "");
        const normalizedQuantity = parseNumericLike(initialQuantity) ?? 1;
        setdefaultCounter(Math.max(1, normalizedQuantity));
    }, [initialSelectedOptions, initialNote, initialQuantity, isOpen, sanitizeSelectedOptions]);

    useEffect(() => {
        setSelectedOptions((prev: any) => sanitizeSelectedOptions(prev));
    }, [sanitizeSelectedOptions]);

    useEffect(() => {
        if (menuOptions.length === 0) {
            return;
        }

        setSelectedOptions((prev: any) => {
            const sanitizedPrev = sanitizeSelectedOptions(prev);
            let changed = false;
            const nextSelections: Record<string | number, any> = { ...sanitizedPrev };

            menuOptions.forEach((option: any) => {
                if (option?.type !== "single") {
                    return;
                }

                if (nextSelections[option.id]) {
                    return;
                }

                const optionValues = Array.isArray(option.values) ? option.values : [];
                const defaultValue = optionValues.find((value: any) => value?.is_default);

                if (defaultValue && getOptionValueAvailability(defaultValue).isAvailable) {
                    nextSelections[option.id] = defaultValue;
                    changed = true;
                }
            });

            return changed ? nextSelections : sanitizedPrev;
        });
    }, [menuOptions, sanitizeSelectedOptions]);

    const handleOptionChange = (optionId: any, value: any, type: string) => {
        const availability = getOptionValueAvailability(value);
        if (!availability.isAvailable) {
            return;
        }

        setSelectedOptions((prev: any) => {
            if (type === "multiple") {
                const existing: any[] = Array.isArray(prev[optionId]) ? prev[optionId] : [];
                const exists = existing.some((v: any) => String(v?.id) === String(value.id));
                const updated = exists
                    ? existing.filter((v: any) => String(v?.id) !== String(value.id))
                    : [...existing, value];

                if (updated.length === 0) {
                    const { [optionId]: _removed, ...rest } = prev || {};
                    return rest;
                }

                return { ...prev, [optionId]: updated };
            }

            const current = prev?.[optionId];
            if (current && String(current?.id) === String(value.id)) {
                return prev;
            }

            return { ...prev, [optionId]: value };
        });
    };

    function countUP(id: any, prev_data_attr: any) {
        const nextValue = Math.max(1, prev_data_attr + 1);
        id(nextValue);
    }
    function countDown(id: any, prev_data_attr: any) {
        const nextValue = Math.max(1, prev_data_attr - 1);
        id(nextValue);
    }

    const basePrice = parseNumericLike(detailShopMenu?.price) ?? 0;
    const isAvailable = isMenuAvailable(detailShopMenu);
    const optionsPrice = Object.values(selectedOptions).reduce((sum: number, opt: any) => {
        if (Array.isArray(opt)) {
            const subtotal = opt.reduce((s: number, v: any) => {
                const availability = getOptionValueAvailability(v);
                if (!availability.isAvailable) {
                    return s;
                }
                return s + (parseNumericLike(v?.price) ?? 0);
            }, 0);
            return sum + subtotal;
        }

        if (!opt || typeof opt !== "object") {
            return sum;
        }

        const availability = getOptionValueAvailability(opt);
        if (!availability.isAvailable) {
            return sum;
        }

        return sum + (parseNumericLike(opt?.price) ?? 0);
    }, 0);
    const itemPrice = basePrice + optionsPrice;
    const totalPrice = itemPrice * defaultCounter;

    const optionGroupBadges = useMemo(() => {
        return menuOptions
            .map((group) => {
                const nameCandidates = [group?.name, group?.title, group?.label, group?.displayName];
                for (const candidate of nameCandidates) {
                    if (typeof candidate === "string" && candidate.trim().length > 0) {
                        return candidate.trim();
                    }
                }
                return "";
            })
            .filter((name): name is string => name.length > 0);
    }, [menuOptions]);

    return (
        <React.Fragment>
            <Offcanvas
               fade
               isOpen={isOpen}
               direction="bottom"
               toggle={onClose}
               id="offcanvasBottom"
               className="min-vh-75"
            >
                <OffcanvasHeader toggle={onClose} id="offcanvasBottomLabel" className="border-bottom">
                    {detailShopMenu?.name} - {toVND(basePrice)}
                </OffcanvasHeader>
                <OffcanvasBody className="pb-5rem">
                    {detailShopMenu?.image && (
                        <img
                            src={detailShopMenu.image}
                            className="w-100"
                            alt={detailShopMenu?.name ?? "menu-image"}
                        />
                    )}
                    <p>{detailShopMenu?.description}</p>
                    {optionGroupBadges.length > 0 && (
                        <div className="mb-3">
                            {optionGroupBadges.map((name) => (
                                <span
                                    key={name}
                                    className="badge bg-primary-subtle text-primary me-1 mb-1"
                                >
                                    {name}
                                </span>
                            ))}
                        </div>
                    )}
                    {!isAvailable && (
                        <Alert color="warning" className="mb-3">
                            Món này hiện đang tạm hết hàng.
                        </Alert>
                    )}
                    {menuOptions.map((option: any) => (
                        <div key={option.id} className="mb-3">
                            <p className="mb-2 fw-bold">{option.name}</p>
                            {option.type === "multiple"
                                ? option.values?.map((value: any) => {
                                      const inputId = `option-${option.id}-${value.id}`;
                                      const availability = getOptionValueAvailability(value);
                                      const isDisabled = !availability.isAvailable;
                                      const statusLabel = availability.statusLabel;
                                      const badgeClass =
                                          availability.reason === "sold_out"
                                              ? "badge bg-danger-subtle text-danger"
                                              : "badge bg-secondary-subtle text-secondary";
                                      const isChecked = Array.isArray(selectedOptions[option.id])
                                          ? selectedOptions[option.id].some(
                                                (v: any) => String(v?.id) === String(value.id)
                                            )
                                          : false;
                                      return (
                                          <div
                                              className="d-flex align-items-center justify-content-between mb-2"
                                              key={value.id}
                                          >
                                              <div className="form-check d-flex align-items-center">
                                                  <input
                                                      className="form-check-input"
                                                      type="checkbox"
                                                      id={inputId}
                                                      checked={isChecked}
                                                      onChange={() =>
                                                          handleOptionChange(option.id, value, option.type)
                                                      }
                                                      disabled={isDisabled}
                                                  />
                                                  <label
                                                      className={`form-check-label ms-2 d-flex align-items-center${
                                                          isDisabled ? " text-muted" : ""
                                                      }`}
                                                      htmlFor={inputId}
                                                  >
                                                      <span>{value.name}</span>
                                                      {statusLabel && (
                                                          <span className={`ms-2 ${badgeClass}`}>
                                                              {statusLabel}
                                                          </span>
                                                      )}
                                                  </label>
                                              </div>
                                              {value.price ? (
                                                  <span className={isDisabled ? "text-muted" : ""}>
                                                      {toVND(value.price)}
                                                  </span>
                                              ) : null}
                                          </div>
                                      );
                                  })
                                : option.values?.map((value: any) => {
                                      const inputId = `option-${option.id}-${value.id}`;
                                      const availability = getOptionValueAvailability(value);
                                      const isDisabled = !availability.isAvailable;
                                      const statusLabel = availability.statusLabel;
                                      const statusTextClass =
                                          availability.reason === "sold_out" ? "text-danger" : "text-muted";
                                      const isChecked =
                                          selectedOptions[option.id] &&
                                          String(selectedOptions[option.id]?.id) === String(value.id);
                                      return (
                                          <div
                                              className="d-flex align-items-center justify-content-between mb-2"
                                              key={value.id}
                                          >
                                              <div className="form-check d-flex align-items-center">
                                                  <input
                                                      className="form-check-input"
                                                      type="radio"
                                                      name={`option-${option.id}`}
                                                      id={inputId}
                                                      autoComplete="off"
                                                      checked={Boolean(isChecked)}
                                                      onChange={() =>
                                                          handleOptionChange(option.id, value, option.type)
                                                      }
                                                      disabled={isDisabled}
                                                  />
                                                  <label
                                                      className={`form-check-label ms-2 d-flex align-items-center${
                                                          isDisabled ? " text-muted" : ""
                                                      }`}
                                                      htmlFor={inputId}
                                                  >
                                                      <span>{value.name}</span>
                                                      {statusLabel && (
                                                          <span className={`ms-2 ${statusTextClass}`}>
                                                              {statusLabel}
                                                          </span>
                                                      )}
                                                  </label>
                                              </div>
                                              {value.price ? (
                                                  <span className={isDisabled ? "text-muted" : ""}>
                                                      {toVND(value.price)}
                                                  </span>
                                              ) : null}
                                          </div>
                                      );
                                  })}
                        </div>
                    ))}
                    <Input
                        type="text"
                        placeholder="Lưu ý cho quán"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        className="mb-3"
                    />
                    {/* Fixed Footer */}
                    <div className="position-fixed bottom-0 start-0 w-100 bg-white border-top py-3">
                        <div className="container">
                            <div className="cart-form d-flex align-items-center justify-content-between">
                                <div className="order-plus-minus d-flex align-items-center">
                                    <button
                                        type="button"
                                        className="quantity-button-handler"
                                        onClick={() => {
                                            if (!isAvailable) {
                                                return;
                                            }
                                            countDown(setdefaultCounter, defaultCounter);
                                        }}
                                        disabled={!isAvailable || defaultCounter <= 1}
                                    >
                                        –
                                    </button>
                                    <Input
                                        type="text"
                                        className="form-control cart-quantity-input"
                                        value={defaultCounter}
                                        min="1"
                                        max="100"
                                        readOnly
                                    />
                                    <button
                                        type="button"
                                        className="quantity-button-handler"
                                        onClick={() => {
                                            if (!isAvailable) {
                                                return;
                                            }
                                            countUP(setdefaultCounter, defaultCounter);
                                        }}
                                        disabled={!isAvailable}
                                    >
                                        +
                                    </button>
                                </div>

                                <Button
                                    color="primary"
                                    className="ms-3"
                                    disabled={!isAvailable}
                                    onClick={() => {
                                        if (!isAvailable) {
                                            return;
                                        }
                                        if (detailShopMenu) {
                                            const item = {
                                                id: detailShopMenu.id,
                                                productName: detailShopMenu.name,
                                                price: itemPrice,
                                                quantity: defaultCounter,
                                                imageUrl: detailShopMenu.image || DEFAULT_PRODUCT_IMAGE,
                                                option: selectedOptions,
                                                note: note,
                                            };
                                            if (onSave) {
                                                onSave(item);
                                            } else {
                                                dispatch(addItem(item));
                                                if (onAddedToCart) {
                                                    onAddedToCart();
                                                }
                                            }
                                        }
                                        if (onClose) {
                                            onClose();
                                        }
                                    }}
                                >
                                    {onSave
                                        ? `Lưu - ${toVND(totalPrice)}`
                                        : `Chọn - ${toVND(totalPrice)}`}
                                </Button>
                            </div>
                        </div>
                    </div>
                </OffcanvasBody>
            </Offcanvas>
        </React.Fragment>
    );
};

export default DetailMenuOffcanvas;
